export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api'
const BASE_URL = API_BASE_URL

// Photo + document CDN. Override with VITE_MEDIA_URL if the bucket changes.
export const MEDIA_URL = import.meta.env.VITE_MEDIA_URL
  || 'https://genealogic-media.s3.eu-central-1.amazonaws.com'

export function mediaUrl(key) {
  if (!key) return null
  return `${MEDIA_URL}/${key}`
}

function getToken() {
  return localStorage.getItem('gl_token')
}

// Transient-network retry. `fetch` rejects with a TypeError on DNS
// failure, dropped connection, or when the API is restarting (pm2 cycle
// during deploy). Without retry the whole page blanks and the user has
// to refresh.
//
// Values chosen to ride out a full pm2 restart (~3–4s cold-start in
// prod): 4 retries with exponential backoff (0.4s → 0.8s → 1.2s → 1.6s,
// total ~4s) before surfacing the error. 502/503/504 are also retried
// because they come back as HTTP errors (not TypeError) during the
// Apache ↔ PM2 handoff window.
const NETWORK_RETRIES = 4
const NETWORK_BACKOFF_MS = 400
const RETRYABLE_STATUSES = new Set([502, 503, 504])

async function fetchWithRetry(url, opts) {
  let lastErr
  for (let attempt = 0; attempt <= NETWORK_RETRIES; attempt++) {
    try {
      const res = await fetch(url, opts)
      // 502/503/504 from Apache during pm2 restart is transient — treat
      // like a network blip and retry. Other HTTP errors surface normally.
      if (RETRYABLE_STATUSES.has(res.status) && attempt < NETWORK_RETRIES) {
        await new Promise(r => setTimeout(r, NETWORK_BACKOFF_MS * (attempt + 1)))
        continue
      }
      return res
    } catch (err) {
      if (!(err instanceof TypeError)) throw err
      lastErr = err
      if (attempt === NETWORK_RETRIES) throw err
      await new Promise(r => setTimeout(r, NETWORK_BACKOFF_MS * (attempt + 1)))
    }
  }
  throw lastErr
}

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' }
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetchWithRetry(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  })

  if (res.status === 204) return null

  // Some error responses (rate limiter, proxy errors, gateway timeouts)
  // come back as text/html or plain text, not JSON. Trying res.json()
  // on those throws a cryptic 'string did not match the expected pattern'
  // in Safari/WebKit. Read as text first, then parse if possible.
  const raw = await res.text()
  let data = {}
  if (raw) {
    try { data = JSON.parse(raw) } catch { data = { error: { message: raw.slice(0, 200) } } }
  }
  if (!res.ok) {
    if (res.status === 429) {
      const err = new Error('Too many requests — slow down for a moment.')
      err.status = 429
      err.code = 'RATE_LIMITED'
      throw err
    }
    const err = new Error(data.error?.message || 'API error')
    err.status = res.status
    // Surface structured error metadata — the API returns `code` (e.g.
    // 'TIER_LIMIT') and `meta` (e.g. { limit, current, kind }) for
    // enforcement failures. Components branch on `err.code` to show
    // an inline upgrade prompt instead of a generic toast.
    if (data.error?.code) err.code = data.error.code
    if (data.error?.meta) err.meta = data.error.meta
    throw err
  }
  return data
}

// Multipart/form-data upload — no Content-Type header (browser sets boundary automatically)
async function upload(path, formData) {
  const headers = {}
  const token = getToken()
  if (token) headers['Authorization'] = `Bearer ${token}`

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    body: formData
  })

  const data = await res.json()
  if (!res.ok) {
    const err = new Error(data.error?.message || 'Upload failed')
    err.status = res.status
    if (data.error?.code) err.code = data.error.code
    if (data.error?.meta) err.meta = data.error.meta
    throw err
  }
  return data
}

export const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  delete: (path, body) => request('DELETE', path, body),
  upload: (path, formData) => upload(path, formData),
}
